import React from 'react';
import { Box, ButtonBase, Tooltip, CircularProgress } from '@mui/material';
import { RocketLaunch, CheckCircle } from '@mui/icons-material';
import { useOnboarding } from '../OnboardingProvider';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Compact onboarding launcher, mounted in the header between HelpDropdown
 * and the profile avatar. Shows progress %, opens the drawer on click,
 * and hides itself once onboarding is complete + 7 days have passed.
 */
export const OnboardingLauncherPill: React.FC = () => {
  const { state, isLoading, drawerOpen, openDrawer } = useOnboarding();

  if (isLoading && !state) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
        <CircularProgress size={16} sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  if (!state) return null;

  const { progress, onboardingCompletedAt } = state;
  const isComplete = progress.percent >= 100;

  // Auto-hide only after celebration window — checklist stays visible
  // even if the user "skips for now" from the drawer.
  if (isComplete && onboardingCompletedAt) {
    const completedAgo = Date.now() - new Date(onboardingCompletedAt).getTime();
    if (completedAgo > SEVEN_DAYS_MS) return null;
  }

  const handleClick = () => {
    openDrawer();
  };

  const bgColor = isComplete ? '#2F7D32' : '#8B4513';
  const label = isComplete ? "You're all set" : `${progress.percent}% setup`;
  const tooltipTitle = isComplete
    ? `Nicely done — ${progress.completed} of ${progress.total} complete`
    : `${progress.completed} of ${progress.total} complete — click to continue`;

  return (
    <Tooltip title={tooltipTitle} arrow>
      <ButtonBase
        onClick={handleClick}
        disabled={drawerOpen}
        aria-label={tooltipTitle}
        data-tour="onboarding-pill"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: 32,
          px: 1.5,
          mr: 1.5,
          borderRadius: 16,
          bgcolor: bgColor,
          color: '#FFFFFF',
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: 0.1,
          boxShadow: '0 1px 2px rgba(59,46,26,0.15)',
          transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
          '&:hover': {
            bgcolor: isComplete ? '#1E6023' : '#6B4F2E',
            boxShadow: '0 2px 6px rgba(59,46,26,0.25)',
            transform: 'translateY(-1px)',
          },
          '&:active': { transform: 'translateY(0)' },
          '&.Mui-disabled': {
            opacity: 0.6,
            color: '#FFFFFF',
          },
        }}
      >
        {isComplete ? (
          <CheckCircle sx={{ fontSize: 16 }} />
        ) : (
          <RocketLaunch sx={{ fontSize: 16 }} />
        )}
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline' },
            lineHeight: 1,
          }}
        >
          {label}
        </Box>
      </ButtonBase>
    </Tooltip>
  );
};
